import queryString from 'query-string';

// External (other job sites) jobs for the /jobs timeline, by posting day.
// Keyed by the board's filter hash plus time zone, since days depend on both.

const LOAD_DAYS = 'twineApp/externalJobs/LOAD_DAYS';
const LOAD_DAYS_SUCCESS = 'twineApp/externalJobs/LOAD_DAYS_SUCCESS';
const LOAD_DAYS_FAIL = 'twineApp/externalJobs/LOAD_DAYS_FAIL';
const LOAD_MORE = 'twineApp/externalJobs/LOAD_MORE';
const LOAD_MORE_SUCCESS = 'twineApp/externalJobs/LOAD_MORE_SUCCESS';
const LOAD_MORE_FAIL = 'twineApp/externalJobs/LOAD_MORE_FAIL';

/** External jobs shown per day before "Show all". */
export const EXTERNAL_JOBS_PER_DAY = 10;

/** Jobs fetched per "Show all" request. */
export const EXTERNAL_JOBS_PER_PAGE = 50;

const initialState = {};

const datesBetween = (from, to) => {
  const dates = [];
  const day = new Date(`${to}T00:00:00Z`);
  const end = new Date(`${from}T00:00:00Z`);
  while (day >= end) {
    dates.push(day.toISOString().slice(0, 10));
    day.setUTCDate(day.getUTCDate() - 1);
  }
  return dates;
};

const entryFor = (state, key) =>
  state[key] || { days: {}, fetched: {}, loading: false };

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case LOAD_DAYS:
      return {
        ...state,
        [action.key]: { ...entryFor(state, action.key), loading: true },
      };

    case LOAD_DAYS_SUCCESS: {
      const entry = entryFor(state, action.key);
      const days = { ...entry.days };
      (action.result.days || []).forEach((day) => {
        days[day.date] = { total: day.total, jobs: day.jobs, loading: false };
      });
      const fetched = { ...entry.fetched };
      // The API clamps long ranges, so trust its range over the request.
      datesBetween(
        action.result.from || action.from,
        action.result.to || action.to
      ).forEach((date) => {
        fetched[date] = true;
      });
      return {
        ...state,
        [action.key]: { ...entry, days, fetched, loading: false },
      };
    }

    case LOAD_DAYS_FAIL: {
      // Mark the range fetched so a failing request isn't retried in a loop;
      // those days just show their Twine jobs.
      const entry = entryFor(state, action.key);
      const fetched = { ...entry.fetched };
      datesBetween(action.from, action.to).forEach((date) => {
        fetched[date] = true;
      });
      return {
        ...state,
        [action.key]: { ...entry, fetched, loading: false, failed: true },
      };
    }

    case LOAD_MORE:
    case LOAD_MORE_FAIL: {
      const entry = entryFor(state, action.key);
      const day = entry.days[action.date];
      if (!day) {
        return state;
      }
      return {
        ...state,
        [action.key]: {
          ...entry,
          days: {
            ...entry.days,
            [action.date]: { ...day, loading: action.type === LOAD_MORE },
          },
        },
      };
    }

    case LOAD_MORE_SUCCESS: {
      const entry = entryFor(state, action.key);
      const day = entry.days[action.date];
      const loaded = (action.result.days || []).find(
        (d) => d.date === action.date
      );
      if (!day) {
        return state;
      }
      const seen = new Set(day.jobs.map((job) => job.id));
      const more = loaded ? loaded.jobs.filter((job) => !seen.has(job.id)) : [];
      return {
        ...state,
        [action.key]: {
          ...entry,
          days: {
            ...entry.days,
            [action.date]: {
              total: loaded ? loaded.total : day.total,
              jobs: [...day.jobs, ...more],
              loading: false,
            },
          },
        },
      };
    }

    default:
      return state;
  }
}

/**
 * Store key for a board filter hash and time zone.
 *
 * @param {string} hash ProjectsBase.getHash() / getBriefsHash()
 * @param {string} timeZone
 * @returns {string}
 */
export const getExternalJobsKey = (hash, timeZone) => `${hash}|${timeZone}`;

/**
 * Board filters as GET /external-jobs params (same names as the Twine list).
 *
 * @param {object} filters
 * @returns {object}
 */
export const buildExternalJobsFilterParams = ({
  roles,
  experienceLevels,
  hiringCapacities,
  remote,
  internal,
  jobLocationText,
  searchTerm,
}) => {
  const params = {};
  if (roles && roles.length > 0) {
    params.roles = roles;
  }
  const levels = (experienceLevels || [])
    .filter((l) => l.isChecked)
    .map((l) => l.type);
  if (levels.length > 0) {
    params.experienceLevel = levels;
  }
  const capacities = (hiringCapacities || [])
    .filter((h) => h.isChecked)
    .map((h) => h.type);
  if (capacities.length > 0) {
    params.hiringCapacity = capacities;
  }
  if (remote) {
    params.remote = 1;
  }
  if (internal) {
    params.internal = 1;
  }
  if (jobLocationText) {
    params.inLocation = jobLocationText.replace(/-/g, ' ');
  }
  if (searchTerm) {
    params.searchTerm = searchTerm;
  }
  return params;
};

const externalJobsUrl = (params) =>
  `/external-jobs?${queryString.stringify(params, { arrayFormat: 'bracket' })}`;

/**
 * Load the first jobs and totals for each day from `from` to `to`.
 *
 * @param {string} key getExternalJobsKey()
 * @param {object} filterParams buildExternalJobsFilterParams()
 * @param {{from: string, to: string}} range
 * @param {string} timeZone
 * @returns {object}
 */
export function loadExternalJobDays(key, filterParams, { from, to }, timeZone) {
  return {
    types: [LOAD_DAYS, LOAD_DAYS_SUCCESS, LOAD_DAYS_FAIL],
    promise: (client) =>
      client.get(
        externalJobsUrl({
          ...filterParams,
          from,
          to,
          tz: timeZone,
          per_day: EXTERNAL_JOBS_PER_DAY,
        })
      ),
    key,
    from,
    to,
  };
}

/**
 * Load the next jobs of one day ("Show all").
 *
 * @param {string} key
 * @param {object} filterParams
 * @param {string} date
 * @param {string} timeZone
 * @param {number} offset
 * @returns {object}
 */
export function loadMoreExternalJobs(
  key,
  filterParams,
  date,
  timeZone,
  offset
) {
  return {
    types: [LOAD_MORE, LOAD_MORE_SUCCESS, LOAD_MORE_FAIL],
    promise: (client) =>
      client.get(
        externalJobsUrl({
          ...filterParams,
          from: date,
          to: date,
          tz: timeZone,
          per_day: EXTERNAL_JOBS_PER_PAGE,
          offset,
        })
      ),
    key,
    date,
  };
}
